import api from "./api.service";

export interface ChargeAllocation {
  status: "none" | "partial" | "full";
  years: number[];
  totalAllocatedRatio: number;
}

export interface Invoice {
  id: string;
  invoiceDate: string;
  invoiceAmount: number;
  initialAmount: number;
  fileName: string;
  colocId: string;
  providerId: string;
  createdAt: string;
  updatedAt: string;
  isValidated: boolean;
  dateValidated?: string | null;
  periodStartDate?: string | null;
  periodEndDate?: string | null;
  consumptionQuantity?: number | null;
  consumptionUnit?: string | null;
  /** true = estimée, false = relevée, null/undefined = nature inconnue. */
  consumptionEstimated?: boolean | null;
  provider?: {
    id: string;
    providerName: string;
  };
  chargeAllocation?: ChargeAllocation;
}

export interface InvoiceUpdateData {
  providerId?: string;
  invoiceAmount?: number;
  initialAmount?: number;
  invoiceDate?: string;
  isValidated?: boolean;
  periodStartDate?: string | null;
  periodEndDate?: string | null;
  consumptionQuantity?: number | null;
  consumptionUnit?: string | null;
  consumptionEstimated?: boolean | null;
}

class InvoicesService {
  async getInvoicesByColocId(
    colocId: string,
    sortField: string = "invoiceDate",
    sortOrder: "ASC" | "DESC" = "DESC"
  ): Promise<Invoice[]> {
    const response = await api.get(`/invoices/coloc/${colocId}`, {
      params: { sortField, sortOrder },
    });
    return response.data;
  }

  async getInvoiceById(id: string): Promise<Invoice> {
    const response = await api.get(`/invoices/${id}`);
    return response.data;
  }

  async updateInvoiceAmount(id: string, amount: number): Promise<Invoice> {
    const response = await api.put(`/invoices/${id}/amount`, { amount });
    return response.data;
  }

  async updateInvoiceProvider(id: string, providerId: string): Promise<Invoice> {
    const response = await api.put(`/invoices/${id}/provider`, { providerId });
    return response.data;
  }

  async updateInvoice(id: string, updateData: InvoiceUpdateData): Promise<Invoice> {
    const response = await api.put(`/invoices/${id}`, updateData);
    return response.data;
  }

  async deleteInvoice(id: string): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/invoices/${id}`);
    return response.data;
  }

  async createInvoice(invoiceData: Partial<Invoice>): Promise<Invoice> {
    const response = await api.post("/invoices", invoiceData);
    return response.data;
  }
}

export const invoicesService = new InvoicesService();
